// ── GameTable — V2 two-row moneyline table using V1 atom library ────
// Dual-format odds (46¢ | +117), gradient liquidity bars, butterfly
// ladder expansion. Matches the V1 ValueDashboard trading screen feel.

import { Fragment, useState, memo } from 'react';
import { cn } from '@/lib/infra/utils';
import { Odds } from '@/components/atoms/Odds';
import { LiquidityBar } from '@/components/atoms/LiquidityBar';
import { Th, Td } from '@/components/atoms/TableCells';
import { SortableTh, type SortDirection } from '@/components/atoms/SortableTh';
import type { GameRow, MarketType, OddsMode, TeamSide } from './types';

// ── Props ────────────────────────────────────────────────────────────

interface GameTableProps {
  games: GameRow[];
  seriesTicker: string;
  marketType: MarketType;
  oddsMode: OddsMode;
  expandedGameKey: string | null;
  onToggleExpand: (key: string, side: TeamSide) => void;
  onTradeClick?: (
    gameKey: string,
    side: 'away' | 'home',
    venue: 'kalshi' | 'polymarket',
    opts?: { priceCents?: number; initialOrderSide?: 'yes' | 'no' },
  ) => void;
}

// ── Helpers ──────────────────────────────────────────────────────────

function formatVolume(vol: number): string {
  if (vol === 0) return '\u2014';
  if (vol >= 1_000_000) return `${(vol / 1_000_000).toFixed(1)}M`;
  if (vol >= 10_000) return `${(vol / 1_000).toFixed(1)}K`;
  if (vol >= 1_000) return vol.toLocaleString();
  return String(vol);
}

const MARKET_TYPE_LABELS: Record<MarketType, string> = {
  moneyline: 'Moneylines',
  spread: 'Spreads',
  total: 'Totals',
  series: 'Series',
};

const MAX_LADDER_LEVELS = 3;

// ── GameTable ────────────────────────────────────────────────────────

export function GameTable({
  games,
  seriesTicker,
  marketType,
  oddsMode,
  expandedGameKey,
  onToggleExpand,
  onTradeClick,
}: GameTableProps) {
  const [sortKey, setSortKey] = useState<string | null>('time');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <div className="bg-card border-border rounded-lg border">
      {/* Title bar */}
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-foreground text-lg font-semibold">
            {games[0]?.sport?.toUpperCase() ?? ''} {MARKET_TYPE_LABELS[marketType]}
          </h2>
          <span className="text-muted-foreground text-xs">&mdash;</span>
          <span className="text-muted-foreground font-mono text-xs">{seriesTicker}</span>
          <span className="text-muted-foreground text-xs">&mdash;</span>
          <span
            className={cn(
              'rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase',
              oddsMode === 'maker' ? 'bg-amber-600/20 text-amber-700 dark:text-amber-400' : 'bg-sky-600/20 text-sky-700 dark:text-sky-400',
            )}
          >
            {oddsMode === 'maker' ? 'Maker' : 'Taker'}
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border-spacing-0">
          <thead className="bg-muted/50 border-border border-b">
            <tr>
              <SortableTh className="w-14" sortKey="roto" activeSortKey={sortKey} activeSortDir={sortDir} onSort={handleSort}>
                Roto
              </SortableTh>
              <SortableTh className="w-24" sortKey="date" activeSortKey={sortKey} activeSortDir={sortDir} onSort={handleSort}>
                Date
              </SortableTh>
              <SortableTh className="w-20" sortKey="time" activeSortKey={sortKey} activeSortDir={sortDir} onSort={handleSort}>
                Time PT
              </SortableTh>
              <SortableTh className="w-48" sortKey="team" activeSortKey={sortKey} activeSortDir={sortDir} onSort={handleSort}>
                Team
              </SortableTh>
              <Th className="w-24 text-right">Kalshi</Th>
              <Th className="w-16 text-right">K Vol</Th>
              <Th className="w-24">K Liq</Th>
              <Th className="w-24 text-right">Poly</Th>
              <Th className="w-16 text-right">P Vol</Th>
              <Th className="w-24">P Liq</Th>
            </tr>
          </thead>
          <tbody>
            {games.map((game, gameIndex) => (
              <GameRows
                key={game.key}
                game={game}
                gameIndex={gameIndex}
                expanded={expandedGameKey === game.key}
                onToggleExpand={onToggleExpand}
                onTradeClick={onTradeClick}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty state */}
      {games.length === 0 && (
        <div className="text-muted-foreground p-8 text-center">No games available</div>
      )}
    </div>
  );
}

// ── GameRows — away/home pair + optional butterfly ladder ────────────

interface GameRowsProps {
  game: GameRow;
  gameIndex: number;
  expanded: boolean;
  onToggleExpand: (key: string, side: TeamSide) => void;
  onTradeClick?: (
    gameKey: string,
    side: 'away' | 'home',
    venue: 'kalshi' | 'polymarket',
    opts?: { priceCents?: number; initialOrderSide?: 'yes' | 'no' },
  ) => void;
}

const GameRows = memo(function GameRows({
  game,
  gameIndex,
  expanded,
  onToggleExpand,
  onTradeClick,
}: GameRowsProps) {

  const renderTeamRow = (teamSide: TeamSide, isFirstRow: boolean) => {
    const teamName = teamSide === 'away' ? game.awayName : game.homeName;
    const kBook = teamSide === 'away' ? game.kalshi?.away : game.kalshi?.home;
    const pBook = teamSide === 'away' ? game.polymarket?.away : game.polymarket?.home;

    // Thick top border between games (not on first game, not when expanded — ladder gets it)
    const borderClass = isFirstRow && gameIndex > 0 && !expanded ? 'border-t-2 border-border' : '';

    // Zebra striping — both rows of a game share the same tint
    const zebraStyle =
      gameIndex % 2 === 1
        ? { backgroundColor: 'hsl(var(--muted) / var(--zebra-opacity))' }
        : undefined;

    return (
      <tr
        key={`${game.key}-${teamSide}`}
        className={cn('hover:bg-accent/50 cursor-pointer transition-colors', borderClass)}
        style={zebraStyle}
        onClick={() => onToggleExpand(game.key, teamSide)}
      >
        {/* Rotation */}
        <Td className="text-muted-foreground font-mono text-xs">
          {teamSide === 'away' ? (game.awayRotation ?? '\u2014') : (game.homeRotation ?? '\u2014')}
        </Td>

        {/* Date (first row only) */}
        <Td className="text-muted-foreground font-mono text-xs">
          {isFirstRow ? game.date : ''}
        </Td>

        {/* Time (first row only) */}
        <Td className="text-muted-foreground font-mono text-xs">
          {isFirstRow ? game.timePt : ''}
        </Td>

        {/* Team */}
        <Td className="font-medium">{teamName}</Td>

        {/* Kalshi odds */}
        <Td
          className={cn('whitespace-nowrap text-right font-mono', kBook?.priceCents != null && 'cursor-pointer hover:underline')}
          onClick={(e: React.MouseEvent<HTMLTableCellElement>) => {
            e.stopPropagation();
            onTradeClick?.(game.key, teamSide, 'kalshi');
          }}
          title={kBook?.priceCents != null ? 'Click to trade' : undefined}
        >
          <Odds cents={kBook?.priceCents} format="dual" />
        </Td>

        {/* K Volume */}
        <Td className="text-muted-foreground text-right font-mono text-xs">
          <span key={kBook?.volume} className="animate-flash-neutral inline-block">
            {kBook ? formatVolume(kBook.volume) : '\u2014'}
          </span>
        </Td>

        {/* K Liquidity */}
        <Td>
          <LiquidityBar value={kBook?.liquidity ?? 0} />
        </Td>

        {/* Poly odds */}
        <Td
          className={cn('whitespace-nowrap text-right font-mono', pBook?.priceCents != null && 'cursor-pointer hover:underline')}
          onClick={(e: React.MouseEvent<HTMLTableCellElement>) => {
            e.stopPropagation();
            onTradeClick?.(game.key, teamSide, 'polymarket');
          }}
          title={pBook?.priceCents != null ? 'Click to trade' : undefined}
        >
          <Odds cents={pBook?.priceCents} format="dual" />
        </Td>

        {/* P Volume */}
        <Td className="text-muted-foreground text-right font-mono text-xs">
          <span key={pBook?.volume} className="animate-flash-neutral inline-block">
            {pBook ? formatVolume(pBook.volume) : '\u2014'}
          </span>
        </Td>

        {/* P Liquidity */}
        <Td>
          <LiquidityBar value={pBook?.liquidity ?? 0} />
        </Td>
      </tr>
    );
  };

  const renderLadderRows = (teamSide: TeamSide, reverse: boolean, showBorder: boolean) => {
    const kBook = teamSide === 'away' ? game.kalshi?.away : game.kalshi?.home;
    const pBook = teamSide === 'away' ? game.polymarket?.away : game.polymarket?.home;
    // Invert NO bids → YES asks (NO bid 82¢ = YES ask 18¢) for team-wins perspective
    const kLevels = (kBook?.no ?? []).slice(0, MAX_LADDER_LEVELS).map(l => ({ priceCents: 100 - l.priceCents, size: l.size }));
    const pLevels = (pBook?.no ?? []).slice(0, MAX_LADDER_LEVELS).map(l => ({ priceCents: 100 - l.priceCents, size: l.size }));

    const maxLen = Math.max(kLevels.length, pLevels.length, MAX_LADDER_LEVELS);
    const indices = reverse
      ? Array.from({ length: maxLen }, (_, i) => maxLen - 1 - i)
      : Array.from({ length: maxLen }, (_, i) => i);

    return indices.map((idx, renderPos) => {
      const kLvl = kLevels[idx] ?? null;
      const pLvl = pLevels[idx] ?? null;

      const kLiq = kLvl ? Math.round((kLvl.priceCents * kLvl.size) / 100) : 0;
      const pLiq = pLvl ? Math.round((pLvl.priceCents * pLvl.size) / 100) : 0;

      // Game separator border on the very first ladder row when expanded
      const borderClass = showBorder && renderPos === 0 ? 'border-t-2 border-border' : '';

      return (
        <tr
          key={`${game.key}-${teamSide}-ladder-${idx}`}
          className={cn('border-0', borderClass)}
        >
          <Td className="py-0.5"> </Td>
          <Td className="py-0.5"> </Td>
          <Td className="py-0.5"> </Td>
          <Td className="py-0.5"> </Td>

          {/* Kalshi ladder price */}
          <Td
            className={cn(
              'whitespace-nowrap py-0.5 text-right font-mono',
              kLvl && 'cursor-pointer hover:underline'
            )}
            onClick={(e: React.MouseEvent<HTMLTableCellElement>) => {
              e.stopPropagation();
              if (kLvl) onTradeClick?.(game.key, teamSide, 'kalshi', {
                priceCents: kLvl.priceCents,
                initialOrderSide: 'no',
              });
            }}
            title={kLvl ? 'Click to trade' : undefined}
          >
            <Odds cents={kLvl?.priceCents ?? null} format="dual" />
          </Td>

          {/* K ladder volume */}
          <Td className="text-muted-foreground py-0.5 text-right font-mono text-xs">
            {kLvl ? formatVolume(kLvl.size) : ''}
          </Td>

          {/* K ladder liquidity */}
          <Td className="py-0.5">
            <LiquidityBar value={kLiq} height={16} />
          </Td>

          {/* Poly ladder price */}
          <Td
            className={cn(
              'whitespace-nowrap py-0.5 text-right font-mono',
              pLvl && 'cursor-pointer hover:underline'
            )}
            onClick={(e: React.MouseEvent<HTMLTableCellElement>) => {
              e.stopPropagation();
              if (pLvl) onTradeClick?.(game.key, teamSide, 'polymarket', {
                priceCents: pLvl.priceCents,
                initialOrderSide: 'no',
              });
            }}
            title={pLvl ? 'Click to trade' : undefined}
          >
            <Odds cents={pLvl?.priceCents ?? null} format="dual" />
          </Td>

          {/* P ladder volume */}
          <Td className="text-muted-foreground py-0.5 text-right font-mono text-xs">
            {pLvl ? formatVolume(pLvl.size) : ''}
          </Td>

          {/* P ladder liquidity */}
          <Td className="py-0.5">
            <LiquidityBar value={pLiq} height={16} />
          </Td>
        </tr>
      );
    });
  };

  return (
    <Fragment>
      {/* Butterfly: away ladders fan upward, team rows in center, home ladders fan downward */}
      {expanded && renderLadderRows('away', true, gameIndex > 0)}
      {renderTeamRow('away', true)}
      {renderTeamRow('home', false)}
      {expanded && renderLadderRows('home', false, false)}
    </Fragment>
  );
});
